// src/pages/Lifestyle.jsx
import React from "react";
import { useNavigate } from "react-router-dom";

export default function Lifestyle() {
  const navigate = useNavigate();

  return (
    <div className="font-sans">

      {/* ================= HERO ================= */}
      <section className="bg-gradient-to-br from-pink-50 via-white to-purple-50 py-24 px-6 text-center">
        <h1 className="text-4xl md:text-5xl font-extrabold text-pink-700 leading-tight">
          Daily Habits That Support Healthy Hormones
        </h1>

        <p className="mt-6 max-w-3xl mx-auto text-gray-700 text-lg md:text-xl">
          Hormones respond strongly to how you sleep, eat, move, and manage stress.
          Small daily habits can create powerful long-term hormonal balance.
        </p>

      </section>

      {/* ================= CONTENT ================= */}
      <section className="max-w-4xl mx-auto px-6 py-16 space-y-20 text-gray-700">

        {/* 1️⃣ Why Habits Matter */}
        <div className="flex flex-col md:flex-row items-start gap-6">
          <img
            src="/assets/4.png"
            alt="Healthy Lifestyle"
            className="w-full md:w-1/4 rounded-xl shadow-lg"
          />
          <div className="flex-1">
            <h2 className="text-2xl md:text-3xl font-bold text-pink-600 mb-4">
              Why Daily Habits Matter for Hormones
            </h2>
            <p className="text-lg md:text-xl mb-3">
              Hormones control mood, metabolism, sleep, appetite, and reproductive health.
              Unlike quick fixes, daily habits influence hormones gradually and naturally.
            </p>
            <p className="text-lg md:text-xl">
              Poor sleep, chronic stress, and unstable eating patterns can disrupt estrogen,
              cortisol, insulin, and thyroid hormones — especially in women.
            </p>
          </div>
        </div>

        {/* 2️⃣ Daily Habits */}
        <div className="flex flex-col md:flex-row-reverse items-start gap-6">
          <img
            src="/assets/4.png"
            alt="Daily Healthy Habits"
            className="w-full md:w-1/2 rounded-xl shadow-lg"
          />
          <div className="flex-1">
            <h2 className="text-2xl md:text-3xl font-bold text-pink-600 mb-4">
              Simple Daily Habits for Hormonal Balance
            </h2>
            <ul className="list-disc list-inside ml-4 text-lg md:text-xl space-y-3">
              <li>Sleep 7–9 hours to regulate cortisol & insulin</li>
              <li>Eat protein-rich meals to stabilize blood sugar</li>
              <li>Limit refined sugar & ultra-processed foods</li>
              <li>Walk, stretch, or do gentle workouts daily</li>
              <li>Practice stress management (breathing, prayer, journaling)</li>
              <li>Take breaks and prioritize emotional well-being</li>
            </ul>
          </div>
        </div>

        {/* 3️⃣ MEDICINE & SUPPLEMENT SUPPORT */}
        <div className="bg-white rounded-2xl p-8 shadow-lg">
          <h2 className="text-2xl md:text-3xl font-bold text-pink-600 mb-4">
            Medicines & Supplements That Support Healthy Habits
          </h2>

          <p className="text-lg md:text-xl mb-4">
            While lifestyle habits are the foundation of hormonal health,
            certain supplements can help enhance energy, reduce inflammation, and support heart and brain health.
          </p>

          <ul className="list-disc list-inside ml-4 text-lg md:text-xl space-y-3">
            <li>
              <strong>Magnesium:</strong> Supports sleep quality, stress reduction, and muscle relaxation.
            </li>
            <li>
              <strong>Vitamin D:</strong> Helps regulate mood, immunity, and hormone signaling.
            </li>
            <li>
              <strong>Omega-3 Fish Oil:</strong> Supports heart health, reduces inflammation, and helps regulate hormones naturally.
            </li>
            <li>
              <strong>B-Complex Vitamins:</strong> Aid energy production and nervous system support.
            </li>
            <li>
              <strong>Herbal Teas (Chamomile, Spearmint):</strong> Often used for relaxation and hormone comfort.
            </li>
          </ul>

          {/* ===== FISH OIL HIGHLIGHT BOX ===== */}
          <div className="bg-blue-50 rounded-2xl p-6 mt-6 shadow-md text-center">
            <h3 className="text-2xl md:text-3xl font-bold text-blue-700 mb-3">
              Omega-3 Fish Oil
            </h3>
            <p className="text-lg md:text-xl mb-4">
              Fish oil supplements are rich in EPA and DHA, which support heart, brain, and hormonal health. Ideal for women aiming for overall wellness.
            </p>
            <img
              src="/assets/OMEGA.jpg"
              alt="Fish Oil Supplement"
              className="w-32 mx-auto mb-4 rounded-xl shadow-md"
            />
            <button
              onClick={() => navigate("/category/supplements")}
              className="bg-blue-500 text-white px-6 py-2 rounded-full font-semibold hover:bg-blue-600 transition"
            >
              View Fish Oil Picks →
            </button>
          </div>
        </div>

      </section>

      {/* ================= DISCLAIMER ================= */}
      <section className="bg-gray-50 py-12 px-6 text-center">
        <p className="max-w-3xl mx-auto text-sm text-gray-600">
          This content is for educational purposes only and does not replace professional medical advice.
          As an Amazon Associate, Glow Picks may earn from qualifying purchases.
        </p>
      </section>
    </div>
  );
}
